/**
 * Shared row context for the timeline (ticket 3525): shared callbacks and
 * disclosure state propagate through LegendList's memo boundaries. Modeled
 * on upstream `MessagesTimeline.tsx` (pin e3e7cc3f) with forge's GitHub
 * blocker and revert-callback surface.
 *
 * @module TimelineRowContext
 */

import type { ServerProviderSkill } from "@cmmd-forge/contracts";
import { type EnvironmentId, type MessageId, type TurnId } from "@cmmd-forge/contracts";
import { createContext } from "react";
import type {
  GitHubActionRequiredRequest,
  GitHubToolConfirmationRequest,
} from "../../session-logic";
import type { TimestampFormat } from "@cmmd-forge/contracts/settings";
import type { ExpandedImagePreview } from "./ExpandedImagePreview";

/**
 * Shared state consumed by every timeline row via useContext. Propagates
 * through LegendList's memo boundaries for shared callbacks and non-row-scoped
 * state. `nowIso` is intentionally excluded: self-ticking components handle it.
 */
export interface TimelineRowSharedState {
  activeTurnInProgress: boolean;
  activeTurnId: TurnId | null | undefined;
  /** Turn id of the latest diff summary, for the changed-files preview pin. */
  latestTurnDiffId: string | null;
  isWorking: boolean;
  isRevertingCheckpoint: boolean;
  timestampFormat: TimestampFormat;
  routeThreadKey: string;
  markdownCwd: string | undefined;
  providerSkills: ReadonlyArray<ServerProviderSkill>;
  resolvedTheme: "light" | "dark";
  workspaceRoot: string | undefined;
  activeThreadEnvironmentId: EnvironmentId;
  onRevertUserMessage: (messageId: MessageId) => void;
  onImageExpand: (preview: ExpandedImagePreview) => void;
  onOpenTurnDiff: (turnId: TurnId, filePath?: string) => void;
  onApproveGitHubConfirmation: (confirmation: GitHubToolConfirmationRequest) => Promise<void>;
  onRetryGitHubActionRequired: (actionRequired: GitHubActionRequiredRequest) => Promise<void>;
  // The blocker currently shown in the composer-anchored panel; its matching
  // transcript row is suppressed so the ask appears once (composer, not both).
  activeActionRequired: GitHubActionRequiredRequest | null;
  suppressedGitHubConfirmationId: string | null;
  declinedGitHubConfirmationId: string | null;
  // Disclosure state and toggles for the ported row kinds (ticket 3525).
  expandedTurnIds: ReadonlySet<TurnId>;
  expandedWorkGroupIds: ReadonlySet<string>;
  expandedSpawnEntryIds: ReadonlySet<string>;
  expandedReasoningMessageIds: ReadonlySet<string>;
  onToggleTurnFold: (turnId: TurnId) => void;
  onToggleWorkGroup: (groupId: string) => void;
  onToggleWorkEntry: (anchorKey: string, collapsed: boolean) => void;
  onToggleSpawnRow: (entryId: string, expanded: boolean) => void;
  onToggleReasoning: (messageId: string, expanded: boolean) => void;
  /** Nested tool-group scroll and disclosure state, keyed by expanded row. */
  workGroupViewState: WorkGroupViewState;
}

export interface WorkGroupViewState {
  scrollPositions: Map<string, { readonly entryId: string; readonly offset: number }>;
  expandedEntries: Set<string>;
}

export interface TimelineRowActivityState {
  latestTurnId: string | null;
  /** The turn still running (or not yet recorded complete), from the deriver. */
  unsettledTurnId: string | null;
  isWorking: boolean;
}

export const TimelineRowCtx = createContext<TimelineRowSharedState>(null!);
export const TimelineRowActivityCtx = createContext<TimelineRowActivityState>(null!);
